import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Typography, Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { fetchPaperSizes } from '../../utils/paperSizeAPI';
import Navbar from '../setup/Navbar';

const CashCountersManagement = () => {
  const user = useSelector((state) => state.auth.user);
  const [counters, setCounters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [paperSizes, setPaperSizes] = useState([
    { sizeID: 1, sizeName: '58mm Thermal', paperMm: 58, charsPerLine: 32 },
    { sizeID: 2, sizeName: '70mm Thermal', paperMm: 70, charsPerLine: 42 },
    { sizeID: 3, sizeName: '80mm Thermal', paperMm: 80, charsPerLine: 48 }
  ]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [currentCounter, setCurrentCounter] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    branchID: '',
    counterName: '',
    printerName: '',
    printerIP: '',
    printerPort: 9100,
    printerPaperMm: 80,
    printerType: 'network',
    status: 'active',
    remarks: ''
  });

  const fetchBranches = async () => {
    try {
      const response = await axios.post(`${global.SET_API_URL}branches/branchList`, { getOrder: true });
      setBranches(response.data || []);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load branches');
    }
  };

  const fetchCounters = async () => {
    try {
      const response = await axios.get(`${global.SET_API_URL}cashCounters/`);
      console.log('Fetched counters:', response.data);
      if (response.data.success) {
        setCounters(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching counters:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to load counters';
      setError(`Database Error: ${errorMsg}. Please run the SQL migration script first.`);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.post(`${global.SET_API_URL}users/getAllUsers`, {
        cmpID: user?.cmpID || 1,
        includeAll: true
      });
      console.log('Fetched users:', response.data);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    }
  }, [user?.cmpID]);

  useEffect(() => {
    fetchBranches();
    fetchCounters();
    loadPaperSizes();
  }, []);

  const loadPaperSizes = async () => {
    try {
      const sizes = await fetchPaperSizes();
      setPaperSizes(sizes);
    } catch (error) {
      console.error('Error loading paper sizes:', error);
    }
  };

  useEffect(() => {
    if (user?.cmpID) {
      fetchUsers();
    }
  }, [user?.cmpID, fetchUsers]);

  const handleOpenDialog = (counter = null) => {
    if (counter) {
      setCurrentCounter(counter);
      setFormData({
        branchID: counter.branchID || '',
        counterName: counter.counterName,
        printerName: counter.printerName,
        printerIP: counter.printerIP,
        printerPort: counter.printerPort,
        printerPaperMm: counter.printerPaperMm || 80,
        printerType: counter.printerType,
        status: counter.status,
        remarks: counter.remarks || ''
      });
    } else {
      setCurrentCounter(null);
      setFormData({
        branchID: branches[0]?.branchID || '',
        counterName: '',
        printerName: '',
        printerIP: '',
        printerPort: 9100,
        printerPaperMm: 80,
        printerType: 'network',
        status: 'active',
        remarks: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCounter(null);
    setError('');
  };

  const handleSaveCounter = async () => {
    try {
      if (currentCounter) {
        await axios.put(
          `${global.SET_API_URL}cashCounters/${currentCounter.counterID}`,
          formData
        );
        setSuccess('Counter updated successfully');
      } else {
        await axios.post(`${global.SET_API_URL}cashCounters/`, formData);
        setSuccess('Counter created successfully');
      }
      fetchCounters();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save counter');
    }
  };

  const handleDeleteCounter = async (counterID) => {
    if (!window.confirm('Are you sure you want to delete this counter?')) return;
    
    try {
      await axios.delete(`${global.SET_API_URL}cashCounters/${counterID}`);
      setSuccess('Counter deleted successfully');
      fetchCounters();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to delete counter');
    }
  };

  const handleAssignCounter = async () => {
    try {
      await axios.post(`${global.SET_API_URL}cashCounters/assignToUser`, {
        userID: selectedUser.userID,
        counterID: selectedUser.newCounterID || null
      });
      setSuccess('Counter assigned successfully');
      setOpenAssignDialog(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh users list to show updated assignment
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to assign counter');
    }
  };

  return (
    <Box>
      <Navbar />
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5">Cash Counters Management</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" href="/UserBranchAssignments">
              Assign Users to Branches
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Add Counter
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {counters.length === 0 && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No counters found. If this is a fresh installation, please run the SQL migration script:
            <br />
            <code>MySqlScripts/cash_counters_schema.sql</code>
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Counter Name</TableCell>
                <TableCell>Printer Name</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Port</TableCell>
                <TableCell>Paper (mm)</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {counters.map((counter) => (
                <TableRow key={counter.counterID}>
                  <TableCell>{counter.counterName}</TableCell>
                  <TableCell>{counter.printerName}</TableCell>
                  <TableCell>{counter.printerIP}</TableCell>
                  <TableCell>{counter.printerPort}</TableCell>
                  <TableCell>{counter.printerPaperMm || 80}</TableCell>
                  <TableCell>{counter.printerType}</TableCell>
                  <TableCell>{counter.status}</TableCell>
                  <TableCell>{counter.remarks}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(counter)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteCounter(counter.counterID)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="h5" sx={{ mb: 2 }}>User Counter Assignments</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Name</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Assigned Counter</TableCell>
                <TableCell>Printer Details</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const assignedCounter = counters.find(c => c.counterID === user.assignedCounterId);
                console.log('User:', user.userName, 'assignedCounterId:', user.assignedCounterId, 'Found counter:', assignedCounter);
                return (
                <TableRow key={user.userID}>
                  <TableCell>{user.userName}</TableCell>
                  <TableCell>{user.dispName}</TableCell>
                  <TableCell>
                    {assignedCounter ? (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {assignedCounter.counterName}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Not Assigned</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {assignedCounter ? (
                      <Box>
                        <Typography variant="caption" display="block">
                          {assignedCounter.printerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {assignedCounter.printerIP}:{assignedCounter.printerPort}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedUser({
                          ...user,
                          newCounterID: user.assignedCounterId
                        });
                        setOpenAssignDialog(true);
                      }}
                    >
                      {assignedCounter ? 'Change' : 'Assign'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Counter Form Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {currentCounter ? 'Edit Counter' : 'Add New Counter'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl required>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={formData.branchID}
                  label="Branch"
                  onChange={(e) => setFormData({ ...formData, branchID: e.target.value })}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.branchID} value={branch.branchID}>
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Counter Name"
                value={formData.counterName}
                onChange={(e) => setFormData({ ...formData, counterName: e.target.value })}
                required
              />
              <TextField
                label="Printer Name"
                value={formData.printerName}
                onChange={(e) => setFormData({ ...formData, printerName: e.target.value })}
                required
              />
              <TextField
                label="Printer IP Address"
                value={formData.printerIP}
                onChange={(e) => setFormData({ ...formData, printerIP: e.target.value })}
              />
              <TextField
                label="Printer Port"
                type="number"
                value={formData.printerPort}
                onChange={(e) => setFormData({ ...formData, printerPort: e.target.value })}
              />
              <FormControl>
                <InputLabel>Printer Paper (mm)</InputLabel>
                <Select
                  value={formData.printerPaperMm}
                  onChange={(e) => setFormData({ ...formData, printerPaperMm: Number(e.target.value) })}
                >
                  {paperSizes.map((size) => (
                    <MenuItem key={size.sizeID} value={size.paperMm}>
                      {size.sizeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Printer Type</InputLabel>
                <Select
                  value={formData.printerType}
                  onChange={(e) => setFormData({ ...formData, printerType: e.target.value })}
                >
                  <MenuItem value="network">Network</MenuItem>
                  <MenuItem value="usb">USB</MenuItem>
                  <MenuItem value="shared">Shared</MenuItem>
                </Select>
              </FormControl>
              <FormControl>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveCounter} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Counter Dialog */}
        <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
          <DialogTitle>Assign Counter to User</DialogTitle>
          <DialogContent>
            <Box sx={{ minWidth: 300, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                User: {selectedUser?.dispName}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Counter</InputLabel>
                <Select
                  value={selectedUser?.newCounterID || ''}
                  onChange={(e) => setSelectedUser({
                    ...selectedUser,
                    newCounterID: e.target.value
                  })}
                >
                  <MenuItem value="">None (Unassign)</MenuItem>
                  {counters
                    .filter(c => c.status === 'active')
                    .map(counter => (
                      <MenuItem key={counter.counterID} value={counter.counterID}>
                        {counter.counterName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignCounter} variant="contained">
              Assign
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CashCountersManagement;

